import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { supabase } from "@/integrations/supabase/client";
import type { Database } from "@/integrations/supabase/types";

export type Profile = Database["public"]["Tables"]["profiles"]["Row"];

const AVATAR_BUCKET = "profile-pictures";
const MAX_AVATAR_BYTES = 2 * 1024 * 1024;

interface ProfileUpdate {
  full_name?: string;
  phone?: string | null;
}

interface PasswordUpdate {
  currentPassword: string;
  newPassword: string;
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

/**
 * Owns all read/write logic for the signed-in user's account: the `profiles`
 * row (name, phone) plus auth-level email and password changes.
 */
export function useProfile() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const userId = user?.id;

  const profileQuery = useQuery({
    queryKey: ["profile", userId],
    queryFn: async () => {
      if (!userId) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("user_id", userId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!userId,
  });

  const updateProfile = useMutation({
    mutationFn: async (values: ProfileUpdate) => {
      if (!userId) throw new Error("Not signed in");
      // Only the keys present in `values` are written, so saving one field
      // never clobbers another.
      const { error } = await supabase
        .from("profiles")
        .update(values)
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      toast.success("Profile updated");
    },
    onError: (err: unknown) => toast.error(errorMessage(err, "Failed to update profile")),
  });

  const uploadAvatar = useMutation({
    mutationFn: async (file: File) => {
      if (!userId) throw new Error("Not signed in");
      if (!file.type.startsWith("image/")) throw new Error("Please choose an image file");
      if (file.size > MAX_AVATAR_BYTES) throw new Error("Image must be under 2MB");

      // One object per user, always overwritten. The folder is the user id so
      // storage RLS can verify ownership on write.
      const path = `${userId}/avatar`;
      const { error: uploadError } = await supabase.storage
        .from(AVATAR_BUCKET)
        .upload(path, file, { upsert: true, contentType: file.type });
      if (uploadError) throw uploadError;

      const { data: urlData } = supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path);
      // Cache-bust so the overwritten image refreshes instead of serving a stale copy.
      const publicUrl = `${urlData.publicUrl}?v=${Date.now()}`;

      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: publicUrl })
        .eq("user_id", userId);
      if (error) throw error;
      return publicUrl;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      toast.success("Profile picture updated");
    },
    onError: (err: unknown) => toast.error(errorMessage(err, "Failed to upload picture")),
  });

  const removeAvatar = useMutation({
    mutationFn: async () => {
      if (!userId) throw new Error("Not signed in");
      await supabase.storage.from(AVATAR_BUCKET).remove([`${userId}/avatar`]);
      const { error } = await supabase
        .from("profiles")
        .update({ avatar_url: null })
        .eq("user_id", userId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["profile", userId] });
      toast.success("Profile picture removed");
    },
    onError: (err: unknown) => toast.error(errorMessage(err, "Failed to remove picture")),
  });

  const updateEmail = useMutation({
    mutationFn: async (email: string) => {
      const { error } = await supabase.auth.updateUser({ email });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Confirmation sent to your new email address"),
    onError: (err: unknown) => toast.error(errorMessage(err, "Failed to update email")),
  });

  const updatePassword = useMutation({
    mutationFn: async ({ currentPassword, newPassword }: PasswordUpdate) => {
      const email = user?.email;
      if (!email) throw new Error("Not signed in");
      // Re-authenticate to verify the current password before changing it.
      const { error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password: currentPassword,
      });
      if (signInError) throw new Error("Current password is incorrect");
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
    },
    onSuccess: () => toast.success("Password updated"),
    onError: (err: unknown) => toast.error(errorMessage(err, "Failed to update password")),
  });

  return {
    profile: profileQuery.data ?? null,
    isLoading: profileQuery.isLoading,
    updateProfile,
    uploadAvatar,
    removeAvatar,
    updateEmail,
    updatePassword,
  };
}
